import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { useRecentFixturesStore } from '@/store/recentFixturesStore';
import { RecentFixtureItem } from '@/components/fixtures/RecentFixtureItem';
import { RecentFixturesSkeleton } from './RecentFixturesSkeleton';
import { DailyReportSectionHeader } from './DailyReportSectionHeader';

interface RecentFixturesPreviewProps {
  maxItems?: number;
  mobileMaxItems?: number;
}

export function RecentFixturesPreview({
  maxItems = 5,
  mobileMaxItems = 3,
}: RecentFixturesPreviewProps) {
  const { fixtures, loading, error, fetchFixtures } = useRecentFixturesStore();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchFixtures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get top fixtures sorted by reportedTime DESC
  const displayFixtures = useMemo(() => {
    const sorted = [...fixtures].sort(
      (a, b) => new Date(b.reportedTime).getTime() - new Date(a.reportedTime).getTime()
    );
    const limit = isMobile ? mobileMaxItems : maxItems;
    return sorted.slice(0, limit);
  }, [fixtures, maxItems, mobileMaxItems, isMobile]);

  const handleRefresh = () => {
    fetchFixtures();
  };

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/10">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      <DailyReportSectionHeader
        title="Recent Fixtures"
        action={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh recent fixtures"
              className="!min-h-9 !min-w-9 !px-2 !py-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Link to="/recent-fixtures" className="inline-flex shrink-0">
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
                className="whitespace-nowrap !px-2 !py-2 sm:!px-3"
                title="View all recent fixtures"
              >
                View all
              </Button>
            </Link>
          </>
        }
      />

      {loading ? (
        <RecentFixturesSkeleton />
      ) : (
        <Card noPadding className="overflow-hidden shadow-sm">
          <div className="divide-y divide-gray-100 dark:divide-dark-700">
            {displayFixtures.map((fixture) => (
              <div key={fixture.fixtureId} className="px-1 py-0.5 sm:px-2 sm:py-1">
                <RecentFixtureItem fixture={fixture} hideMoreDetails embedded />
              </div>
            ))}
          </div>

          {displayFixtures.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No recent fixtures available
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
